import { useCallback, useEffect } from 'react';
import { routes, useDataTracking } from '@cloud/shared';
import { useLocation } from 'react-router-dom';

const routeToGAEventMapping = {
    [routes.statusPage]: { id: 'status', topLevel: true },
    [routes.users]: { id: 'users' },
    [routes.groups]: { id: 'groups' },
    [routes.devices]: { id: 'devices' },
    [routes.locationContext.all]: { id: 'location_context' },
    [routes.devicePosture.all]: { id: 'device_posture' },

    [routes.networks]: { id: 'networks' },
    [routes.networkApplications]: { id: 'applications_networks' },
    [routes.networkIPServices]: { id: 'ip_services_networks' },
    [routes.networkConnectors]: { id: 'connectors_networks' },

    [routes.hosts]: { id: 'hosts' },
    [routes.hostApplications]: { id: 'applications_hosts' },
    [routes.hostIPServices]: { id: 'ip_services_hosts' },
    [routes.hostConnectors]: { id: 'connectors_hosts' },

    [routes.accessVisibility]: { id: 'visibility_access' },
    [routes.accessControl]: { id: 'groups_access' },
    [routes.accessInternet.groups]: { id: 'internet_access' },

    [routes.cyberShieldV2.overview]: { id: 'overview_shield' },
    [routes.cyberShieldV2.configuration]: { id: 'configuration_shield' },
    [routes.cyberShieldV2.metrics]: { id: 'metrics_shield' },

    [routes.publishing]: { id: 'apphub', topLevel: true },

    [routes.ssoApi]: { id: 'api' },
    [routes.auditLog]: { id: 'audit_log' },
    [routes.dnsResolutions]: { id: 'dns_log' },
    [routes.logStreaming]: { id: 'log_streaming' },

    [routes.networkSettings]: { id: 'wpc_settings' },
    [routes.userSettings]: { id: 'users_settings' },
    [routes.userAuth]: { id: 'user_authentication_settings' },
    [routes.dnsServers]: { id: 'dns_settings' },
    [routes.notificationPreferences]: { id: 'notifications_settings' },

    [routes.wizard.restart]: { id: 'restart_onboarding' },
    [routes.quickStart]: { id: 'quick_start' },
    [window._env_.DOCUMENTATION_LINK]: { id: 'knowledge_base' },
    [window._env_.SUPPORT_HOST]: { id: 'support' },

    [routes.getConnectedUnified]: { id: 'download_app', topLevel: true },
} as Record<string, { id: string; topLevel?: boolean }>;

export function useSidebarAnalytics() {
    const { pathname } = useLocation();
    const trackEvent = useDataTracking();

    const handleSidebarItemClick = useCallback(
        (_, linkProps: { to: string; href?: string }) => {
            const eventParams = routeToGAEventMapping[linkProps.to || linkProps.href || ''];

            if (eventParams) {
                trackEvent({
                    event: eventParams.topLevel ? 'main_navigation' : 'sub_navigation',
                    menu_item: eventParams.id,
                });
            }

            if (linkProps.to === routes.wizard.restart) {
                trackEvent({
                    event: 'restart_onboarding',
                });
            }
        },
        [trackEvent],
    );

    useEffect(() => {
        trackEvent({
            event: 'pageview',
            page_url: pathname,
        });
    }, [trackEvent, pathname]);

    useEffect(() => {
        const observer = new MutationObserver(function (mutations) {
            mutations.forEach(function (mutationRecord) {
                const el = mutationRecord.target as Element;
                if (!el || !el.classList) {
                    return;
                }

                const target = mutationRecord.target;

                if (
                    target instanceof Element &&
                    (el.classList.contains('expanded') || el.classList.contains('collapsed'))
                ) {
                    const iconEl = target.querySelector('.ovpn-sidebar-link-icon svg');
                    const dataName = iconEl?.getAttribute('data-name');

                    trackEvent({
                        event: 'main_navigation',
                        menu_item: dataName,
                    });
                }
            });
        });

        const target = document.querySelector('.ovpn-sidebar-links');

        if (!target) {
            return;
        }

        observer.observe(target, {
            subtree: true,
            attributes: true,
            attributeFilter: ['class'],
        });

        return () => {
            observer.disconnect();
        };
    }, [trackEvent]);

    return {
        handleSidebarItemClick,
    };
}
